import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { microphoneOwner, useMicrophoneOwner } from "./microphoneOwnership";

export interface SelectedEncounter {
  patientId: string;
  encounterId: string;
  assignedClinician?: string;
  label?: string;
}
export interface EncounterTab extends SelectedEncounter {
  /** Random session-local handle; patient and encounter IDs never enter history. */
  tabId: string;
}
type PendingEncounterSwitch =
  | { readonly kind: "selection"; readonly selection: SelectedEncounter }
  | { readonly kind: "deferred"; readonly prepare: () => SelectedEncounter }
  | { readonly kind: "close"; readonly encounterId: string };

interface EncounterSelectionContextValue {
  selectedEncounter: EncounterTab | null;
  encounterTabs: readonly EncounterTab[];
  selectEncounter(selection: SelectedEncounter): boolean;
  selectEncounterDeferred(prepare: () => SelectedEncounter): boolean;
  closeEncounterTab(encounterId: string): boolean;
  clearEncounterSelection(): void;
  pendingEncounterSwitch: boolean;
  microphoneBusy: boolean;
  confirmEncounterSwitch(): SelectedEncounter | null;
  cancelEncounterSwitch(): void;
}
const EncounterSelectionContext = createContext<EncounterSelectionContextValue | null>(null);

export function EncounterSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedEncounter, setSelectedEncounter] = useState<EncounterTab | null>(null);
  const [encounterTabs, setEncounterTabs] = useState<readonly EncounterTab[]>([]);
  const [pending, setPending] = useState<PendingEncounterSwitch | null>(null);
  const selectedRef = useRef<EncounterTab | null>(null);
  const tabsRef = useRef<readonly EncounterTab[]>([]);
  const microphone = useMicrophoneOwner();
  const applySelection = useCallback((selection: SelectedEncounter) => {
    const existing = tabsRef.current.find((tab) => tab.encounterId === selection.encounterId);
    // A caller may not rebind an existing encounter to a different patient.
    if (existing && existing.patientId !== selection.patientId)
      throw new Error("The encounter does not belong to this patient.");
    const tab: EncounterTab = existing
      ? { ...existing, ...selection }
      : { ...selection, tabId: crypto.randomUUID().replaceAll("-", "") };
    const tabs = existing
      ? tabsRef.current.map((current) => (current.encounterId === tab.encounterId ? tab : current))
      : [...tabsRef.current, tab];
    tabsRef.current = tabs;
    selectedRef.current = tab;
    setEncounterTabs(tabs);
    setSelectedEncounter(tab);
    setPending(null);
  }, []);

  const selectEncounter = useCallback(
    (selection: SelectedEncounter): boolean => {
      const microphone = microphoneOwner();
      if (microphone && microphone.encounterId !== selection.encounterId) {
        setPending({ kind: "selection", selection });
        return false;
      }
      applySelection(selection);
      return true;
    },
    [applySelection],
  );
  const selectEncounterDeferred = useCallback(
    (prepare: () => SelectedEncounter): boolean => {
      if (microphoneOwner()) {
        setPending({ kind: "deferred", prepare });
        return false;
      }
      applySelection(prepare());
      return true;
    },
    [applySelection],
  );
  const closeEncounterTab = useCallback((encounterId: string): boolean => {
    if (microphoneOwner()?.encounterId === encounterId) {
      setPending({ kind: "close", encounterId });
      return false;
    }
    const tabs = tabsRef.current.filter((tab) => tab.encounterId !== encounterId);
    tabsRef.current = tabs;
    setEncounterTabs(tabs);
    if (selectedRef.current?.encounterId === encounterId) {
      selectedRef.current = tabs.at(-1) ?? null;
      setSelectedEncounter(selectedRef.current);
    }
    setPending(null);
    return true;
  }, []);
  const clearEncounterSelection = useCallback(() => {
    selectedRef.current = null;
    tabsRef.current = [];
    setEncounterTabs([]);
    setSelectedEncounter(null);
    setPending(null);
  }, []);
  const confirmEncounterSwitch = useCallback((): SelectedEncounter | null => {
    if (pending === null || microphoneOwner()) return null;
    if (pending.kind === "close") {
      closeEncounterTab(pending.encounterId);
      return selectedRef.current;
    }
    const selection = pending.kind === "selection" ? pending.selection : pending.prepare();
    applySelection(selection);
    return selection;
  }, [applySelection, closeEncounterTab, pending]);
  const cancelEncounterSwitch = useCallback(() => setPending(null), []);
  const value = useMemo(
    () => ({
      selectedEncounter,
      encounterTabs,
      selectEncounter,
      selectEncounterDeferred,
      closeEncounterTab,
      clearEncounterSelection,
      pendingEncounterSwitch: pending !== null,
      microphoneBusy: microphone !== null,
      confirmEncounterSwitch,
      cancelEncounterSwitch,
    }),
    [
      selectedEncounter,
      encounterTabs,
      selectEncounter,
      selectEncounterDeferred,
      closeEncounterTab,
      clearEncounterSelection,
      pending,
      microphone,
      confirmEncounterSwitch,
      cancelEncounterSwitch,
    ],
  );
  return (
    <EncounterSelectionContext.Provider value={value}>
      {children}
    </EncounterSelectionContext.Provider>
  );
}
export function useOptionalEncounterSelection(): EncounterSelectionContextValue | null {
  return useContext(EncounterSelectionContext);
}
export function useEncounterSelection(): EncounterSelectionContextValue {
  const context = useOptionalEncounterSelection();
  if (!context)
    throw new Error("useEncounterSelection must be used inside EncounterSelectionProvider");
  return context;
}
